import { effect, inject, Injectable, Injector, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import maplibregl, { ControlPosition, IControl, NavigationControlOptions } from 'maplibre-gl';

import { BasemapControl } from '@connect-services/client/components/map/controls/basemap-control';
import { EditModeControl } from '@connect-services/client/components/map/controls/edit-mode-control';
import { GeolocationControl } from '@connect-services/client/components/map/controls/geolocation-control';
import { ImmersiveViewControl } from '@connect-services/client/components/map/controls/immersive-view-control';
import { MapExportControl } from '@connect-services/client/components/map/controls/map-export-control';
import { MeasurementToolControl } from '@connect-services/client/components/map/controls/measurement-tool-control';
import { NavigationControlExtended } from '@connect-services/client/components/map/controls/navigation-control-extended';
import { PinControl } from '@connect-services/client/components/map/controls/pin-control';
import { SelectionModalControl } from '@connect-services/client/components/map/controls/selection-modal-control';
import { AssertService } from '@connect-services/client/services/assert.service';
import { BasemapService } from '@connect-services/client/services/basemap.service';
import {
    ContentLayoutService,
    HORIZONTAL_HOME_GRID_PARAMS,
    VERTICAL_HOME_GRID_PARAMS,
} from '@connect-services/client/services/content-layout.service';
import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import { ImmersiveViewService } from '@connect-services/client/services/immersive-view/immersive-view.service';
import {
    MapModalSelectionModeEnum,
    SelectionService,
} from '@connect-services/client/services/map/selection/selection.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapModalService } from '@connect-services/client/services/map-modal.service';
import { MapToImageService } from '@connect-services/client/services/map-to-image.service';
import { PinService } from '@connect-services/client/services/pin.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';

export type BasemapOptions = {
    enabled: boolean;
    listener?: (isActive: boolean) => void;
};

export type ControlButtonStyle = {
    color: string;
    height: string;
    width: string;
};

export type EditModeOptions = {
    enabled: boolean;
    listener?: () => void;
};

export type GeographicFilterViewOptions = {
    enabled: boolean;
    layerSelectChangeListener?: (selectedSourceIds: string[]) => void;
    selectBottomPosition: string;
    toggleLayerZoneListener?: (isActive: boolean) => void;
};

export type MapExportOptions = {
    enabled: boolean;
    listener?: (isActive: boolean) => void;
};

export type MeasurementOptions = {
    enabled: boolean;
    listener?: (isActive: boolean) => void;
};

export type NavigationOptions = {
    enabled: boolean;
    options?: NavigationControlOptions;
};

export type PinOptions = {
    enabled: boolean;
};

export type SelectionModalOptions = {
    enabled: boolean;
};

export type ImmersiveViewOptions = {
    enabled: boolean;
};

export type AddControlsOptions = {
    basemap?: BasemapOptions;
    draw?: boolean;
    editMode?: EditModeOptions;
    geographicFilter?: GeographicFilterViewOptions;
    geolocate?: boolean;
    immersiveView?: ImmersiveViewOptions;
    mapExport?: MapExportOptions;
    measurement?: MeasurementOptions;
    navigation?: NavigationOptions;
    pins?: PinOptions;
    selectionModal?: SelectionModalOptions;
};

@Injectable({
    providedIn: 'root',
})
export class MapControlService extends MapBaseService {
    public readonly mapExportControl = signal<MapExportControl | null>(null);

    private readonly basemapControl = signal<BasemapControl | null>(null);
    private readonly drawControl = signal<(MapboxDraw & IControl) | null>(null);
    private readonly editModeControl = signal<EditModeControl | null>(null);
    private readonly geolocationControl = signal<GeolocationControl | null>(null);
    private readonly immersiveViewControl = signal<ImmersiveViewControl | null>(null);
    private reorderRequestAnimationFrameId: number | null = null;
    private readonly selectionModalControl = signal<SelectionModalControl | null>(null);
    private readonly selectionModalOptions = signal<SelectionModalOptions | null>(null);
    private readonly selectZoneNewViewOptions = signal<GeographicFilterViewOptions | null>(null);
    private readonly topRightControlOrder: ReadonlyArray<string> = [
        'selection-modal-control',
        'map-export-control',
        'map-edit-mode-control',
    ];

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly basemapService = inject(BasemapService);
    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly geographicFilterService = inject(GeographicFilterService);
    private readonly immersiveViewService = inject(ImmersiveViewService);
    private readonly injector = inject(Injector);
    private readonly mapDrawService = inject(MapDrawService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly mapModalService = inject(MapModalService);
    private readonly mapToImageService = inject(MapToImageService);
    private readonly pinService = inject(PinService);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly selectionService = inject(SelectionService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        super();

        effect(() => {
            if (!this.geographicFilterService.isDrawControlActive()) {
                this.resetDrawControl();
            }
        });

        effect(() => {
            const isDisabled = this.isMapExportDisabled();
            this.mapExportControl()?.updateVisibility(isDisabled);
        });

        effect(() => {
            this.translocoService.getActiveLang();

            this.geolocationControl()?.refreshTooltip();

            requestAnimationFrame(() => {
                this.refreshNativeControlTooltips();
            });
        });

        effect(() => {
            this.immersiveViewService.canUseImmersiveView();
            this.immersiveViewService.isImmersiveViewActive();
            this.immersiveViewService.isPermissionsLoading();
            this.immersiveViewService.immersiveViewPermissions();

            this.immersiveViewControl()?.updateVisibility();
        });
    }

    public addControls(options: AddControlsOptions): void {
        if (!this.isMapPresent()) {
            return;
        }

        if (options.navigation?.enabled) {
            this.map.addControl(
                new NavigationControlExtended(this.translocoService, options.navigation.options),
                'bottom-right',
            );
        }

        if (options.draw) {
            this.addDrawControl();
            const draw = this.getDrawControl();

            this.mapDrawService.draw = draw;

            AssertService.isIControl(draw);
            this.map.addControl(draw, 'top-right');

            this.mapDrawService.addDrawEvent();
        }

        if (options.geographicFilter?.enabled) {
            this.selectZoneNewViewOptions.set(options.geographicFilter);
        }

        if (options.geolocate) {
            this.loadGeolocationControl();
        }

        if (options.basemap?.enabled) {
            this.loadMapBasemapControl(options.basemap);
        }

        if (options.mapExport) {
            this.loadMapExportControl(options.mapExport, false);
        }

        if (options.selectionModal?.enabled) {
            this.selectionModalOptions.set(options.selectionModal);
            this.addSelectionModalControl(false);
        }

        if (options.editMode?.enabled) {
            this.loadMapEditModeControl(options.editMode, false);
        }

        if (options.immersiveView) {
            const immersiveViewControl = new ImmersiveViewControl(
                this.map,
                this.immersiveViewService,
                this.translocoService,
            );

            this.map.addControl(immersiveViewControl, 'bottom-right');
            this.immersiveViewControl.set(immersiveViewControl);

            immersiveViewControl.updateVisibility();
        }

        if (options.measurement?.enabled) {
            const measurementToolControl = new MeasurementToolControl(
                this.map,
                this.getControlButtonStyle(),
                this.mapModalService,
                this.translocoService,
            );

            this.map.addControl(measurementToolControl, 'bottom-right');

            if (options.measurement.listener) {
                measurementToolControl.measurementToolToggled.subscribe(options.measurement.listener);
            }
        }

        if (options.pins?.enabled) {
            const pinControl = new PinControl(this.map, this.pinService, this.translocoService, this.injector);

            this.map.addControl(pinControl, 'bottom-right');
        }

        this.map.addControl(new maplibregl.AttributionControl(), 'bottom-left');

        this.enforceTopRightControlsOrder();
        this.refreshNativeControlTooltips();
    }

    public bindMap(map: maplibregl.Map): void {
        this.map = map;

        this.editModeControl.set(null);
        this.mapExportControl.set(null);
        this.selectionModalControl.set(null);
        this.immersiveViewControl.set(null);
    }

    public getDrawControl(additionalClass: string = ''): MapboxDraw {
        (MapboxDraw.constants.classes.CONTROL_BASE as unknown) = `maplibregl-ctrl ${additionalClass}`;
        // @ts-expect-error To use maplibre css class and not mapbox
        MapboxDraw.constants.classes.CONTROL_GROUP = 'maplibregl-ctrl-group';
        // @ts-expect-error To use maplibre css class and not mapbox
        MapboxDraw.constants.classes.CONTROL_PREFIX = 'maplibregl-ctrl-';

        return new MapboxDraw({
            controls: {
                polygon: true,
                trash: true,
            },
            displayControlsDefault: false,
        });
    }

    public hideSelectionModalControl(): void {
        const selectionModalControl = this.selectionModalControl();
        if (!this.isMapPresent() || !selectionModalControl) {
            return;
        }

        try {
            this.map.removeControl(selectionModalControl);
        } catch {
            /* empty */
        }

        this.selectionModalControl.set(null);
        this.scheduleReorderTopRightControls();
    }

    public loadMapBasemapControl(basemapOption: BasemapOptions): void {
        if (this.basemapControl()) {
            this.map?.removeControl(this.basemapControl()!);
        }

        this.basemapControl.set(null);

        if (!basemapOption.enabled) {
            return;
        }

        const basemapControl = new BasemapControl(this.map!, this.basemapService, this.translocoService, this.injector);
        this.map!.addControl(basemapControl, 'bottom-right');

        this.basemapControl.set(basemapControl);

        if (basemapOption.listener) {
            basemapControl.basemapToggled.subscribe(basemapOption.listener);
        }
    }

    public loadMapEditModeControl(editModeOptions: EditModeOptions, reorder: boolean = true): void {
        if (!this.isMapPresent()) {
            return;
        }

        if (!editModeOptions.enabled) {
            if (this.editModeControl()) {
                try {
                    this.map.removeControl(this.editModeControl()!);
                } catch {
                    /* empty */
                }
                this.editModeControl.set(null);
            }

            if (reorder) {
                this.scheduleReorderTopRightControls();
            }

            return;
        }

        if (!this.editModeControl()) {
            const editModeControl = new EditModeControl(
                this.map,
                this.mapEditModeService,
                this.projectEditModeService,
                this.translocoService,
            );
            this.map.addControl(editModeControl, 'top-right');
            this.editModeControl.set(editModeControl);

            if (editModeOptions.listener) {
                editModeControl.editModeToggled.subscribe(editModeOptions.listener);
            }
        }

        if (reorder) {
            this.scheduleReorderTopRightControls();
        }
    }

    public loadMapExportControl(mapExportOption: MapExportOptions, reorder: boolean = true): void {
        if (this.mapExportControl()) {
            try {
                this.map?.removeControl(this.mapExportControl()!);
            } catch {
                /* empty */
            }
        }

        this.mapExportControl.set(null);

        if (!mapExportOption.enabled || !this.map) {
            if (reorder) {
                this.scheduleReorderTopRightControls();
            }

            return;
        }

        const mapExportControl = new MapExportControl(this.map, this.mapToImageService, this.translocoService);
        this.map.addControl(mapExportControl, 'top-right');

        this.mapExportControl.set(mapExportControl);

        mapExportControl.updateVisibility(this.isMapExportDisabled());

        if (mapExportOption.listener) {
            mapExportControl.mapExportToggled.subscribe(mapExportOption.listener);
        }

        if (reorder) {
            this.scheduleReorderTopRightControls();
        }
    }

    public reloadSelectionModalControl(options: SelectionModalOptions): void {
        if (!this.isMapPresent()) {
            return;
        }

        if (this.selectionModalControl()) {
            this.map.removeControl(this.selectionModalControl()!);
        }

        this.selectionModalControl.set(null);

        if (!options.enabled) {
            this.selectionService.setModalMode(MapModalSelectionModeEnum.SELECTION);
            this.selectionService.clearAllSelections(true);
        } else {
            this.selectionModalOptions.set(options);
            this.addSelectionModalControl(false);
        }

        this.scheduleReorderTopRightControls();
    }

    public resetDrawControl(): void {
        if (!this.drawControl()) {
            return;
        }

        this.drawControl()!.changeMode('simple_select');
        this.drawControl()!.deleteAll();

        if (this.geographicFilterService.isDrawControlActive()) {
            this.geographicFilterService.isDrawControlActive.set(false);
        }
    }

    public scheduleReorderTopRightControls(): void {
        if (!this.isMapPresent()) {
            return;
        }

        if (this.reorderRequestAnimationFrameId !== null) {
            cancelAnimationFrame(this.reorderRequestAnimationFrameId);
        }

        this.reorderRequestAnimationFrameId = requestAnimationFrame(() => {
            this.reorderRequestAnimationFrameId = null;

            if (!this.isMapPresent()) {
                return;
            }

            this.enforceTopRightControlsOrder();
        });
    }

    public showSelectionModalControl(): void {
        if (!this.isMapPresent() || this.selectionModalControl()) {
            return;
        }

        this.addSelectionModalControl(false);
        this.scheduleReorderTopRightControls();
    }

    private addDrawControl(position?: ControlPosition | undefined): void {
        if (!this.isMapPresent()) {
            return;
        }

        const draw = this.getDrawControl();

        AssertService.isIControl(draw);

        this.drawControl.set(draw);
        this.mapDrawService.draw = draw;

        this.map.addControl(draw, position);

        this.mapDrawService.addDrawEvent();

        this.map.on('draw.modechange', (event) => {
            if (event.mode !== 'simple_select') {
                this.geographicFilterService.isDrawControlActive.set(true);
                this.geographicFilterService.isSelectZoneControlActive.set(false);
            }
        });
    }

    private addSelectionModalControl(reorder: boolean = true): void {
        if (!this.map) {
            return;
        }

        const selectionModalControl = new SelectionModalControl(
            this.map,
            this.mapEditModeService,
            this.mapModalService,
            this.selectionService,
            this.translocoService,
            this.injector,
        );

        this.selectionModalControl.set(selectionModalControl);

        this.map.addControl(selectionModalControl, 'top-right');

        if (reorder) {
            this.scheduleReorderTopRightControls();
        }
    }

    private enforceTopRightControlsOrder(): void {
        if (!this.isMapPresent()) {
            return;
        }

        const controlById: Record<string, maplibregl.IControl | null> = {
            'map-edit-mode-control': this.editModeControl(),
            'map-export-control': this.mapExportControl(),
            'selection-modal-control': this.selectionModalControl(),
        };

        for (const id of this.topRightControlOrder) {
            const control = controlById[id];
            if (!control) {
                continue;
            }

            try {
                this.map.removeControl(control);
            } catch {
                /* empty */
            }

            this.map.addControl(control, 'top-right');
        }
    }

    private getControlButtonStyle(color?: string): ControlButtonStyle {
        return {
            color: color ?? '#333333',
            height: '1.25rem',
            width: '1.25rem',
        };
    }

    private isMapExportDisabled(): boolean {
        const allowedMapSizes = [
            HORIZONTAL_HOME_GRID_PARAMS.FULL_SCREEN,
            HORIZONTAL_HOME_GRID_PARAMS.TWO_THIRDS_HORIZONTAL,
            VERTICAL_HOME_GRID_PARAMS.FULL_SCREEN,
            VERTICAL_HOME_GRID_PARAMS.TWO_THIRDS_VERTICAL,
        ];

        return !allowedMapSizes.includes(this.contentLayoutService.mapSizeType());
    }

    private loadGeolocationControl(): void {
        if (!this.map) {
            return;
        }

        if (this.geolocationControl()) {
            this.map.removeControl(this.geolocationControl()!);
            this.geolocationControl.set(null);
        }

        const geolocationControl = new GeolocationControl(this.translocoService);

        this.map.addControl(geolocationControl, 'bottom-right');
        this.geolocationControl.set(geolocationControl);
    }

    private refreshNativeControlTooltips(): void {
        if (!this.map) {
            return;
        }

        const mapContainer = this.map.getContainer();

        const buttons = mapContainer.querySelectorAll<HTMLButtonElement>(
            '.maplibregl-ctrl button[title], .maplibregl-ctrl button.map-control-native-tooltip',
        );

        buttons.forEach((button: HTMLButtonElement): void => {
            const tooltip = button.getAttribute('title') ?? button.getAttribute('aria-label');

            if (!tooltip) {
                return;
            }

            button.dataset['tooltip'] = tooltip;
            button.setAttribute('aria-label', tooltip);
            button.classList.add('map-control-native-tooltip');
            button.removeAttribute('title');
        });
    }
}
